import {
  CREATOR_ID,
  DEFAULT_LPR_DETECTION_CONFIGURATION,
  EEVA_ANALYSED_IMAGE_KEY,
  EEVA_ANALYSED_IMAGE_KEY_LABELS,
  EEVA_PREVIEW_CARD_COLORS,
  LPR_APPLICATION_PROCESSING_RATES,
  LPR_DIRECTIONS,
  PERFORMED_ON,
  UPDATED_CONFIGURATION,
} from './constants';
import {
  ConfigurationPatch,
  CreatorDescription,
  LprConfiguration,
} from '@eencloud/eewc-components/src/service/api-types';

import dayjs from 'dayjs';
import _ from 'lodash';

import { colorWithOpacity } from '@/service/helpers';
import { t, tc } from '@/plugins/i18n';
import { useUsersStore } from '@/stores';

export const getLprDefaultConfiguration = (
  selectedCreator: CreatorDescription,
  creatorInfoConfig: { creatorId: string }
) => {
  const selectedCreatorSchemaProperties = selectedCreator.configuration.schema.properties.properties;

  let currentConfig: LprConfiguration = {
    country: selectedCreatorSchemaProperties?.country.default,
    direction: selectedCreatorSchemaProperties?.direction.default,
    directionLine: selectedCreatorSchemaProperties?.directionLine.default,
    processingRate: selectedCreatorSchemaProperties?.processingRate.default,
    processingResolution: selectedCreatorSchemaProperties?.processingResolution.default,
  };

  const defaultConfigOptions = DEFAULT_LPR_DETECTION_CONFIGURATION.find(
    (item: { creatorId: string }) => item.creatorId === creatorInfoConfig.creatorId
  );

  if (creatorInfoConfig.creatorId === CREATOR_ID.LPR_BRIDGE) {
    currentConfig = {
      ...currentConfig,
      entryDirection: defaultConfigOptions?.entryDirection,
      localId: defaultConfigOptions?.localId,
      webhooks: defaultConfigOptions?.webhooks,
    };
  } else if (creatorInfoConfig.creatorId === CREATOR_ID.LPR_CLOUD) {
    currentConfig = {
      ...currentConfig,
      entryDirection: defaultConfigOptions?.entryDirection,
    };
  }

  return currentConfig;
};

export const getLprApplicationTypeLabelString = (item: number) => {
  switch (true) {
    case item === LPR_APPLICATION_PROCESSING_RATES.GATE:
      return t('Gate LPR ({vehicleSpeed} mph)', { vehicleSpeed: LPR_APPLICATION_PROCESSING_RATES.GATE });
    case item === LPR_APPLICATION_PROCESSING_RATES.STREET:
      return t('Street LPR ({minVehicleSpeed}-{maxVehicleSpeed} mph)', {
        minVehicleSpeed: LPR_APPLICATION_PROCESSING_RATES.GATE,
        maxVehicleSpeed: LPR_APPLICATION_PROCESSING_RATES.HIGHWAY.THIRTY,
      });
    case item === LPR_APPLICATION_PROCESSING_RATES.HIGHWAY.TWENTY:
      return t('Highway LPR ({minVehicleSpeed}-{maxVehicleSpeed} mph)', {
        minVehicleSpeed: LPR_APPLICATION_PROCESSING_RATES.HIGHWAY.THIRTY,
        maxVehicleSpeed: LPR_APPLICATION_PROCESSING_RATES.HIGHWAY.SEVENTY,
      });
  }
};

export const getLprDirectionLabel = (value: string) => {
  switch (value) {
    case LPR_DIRECTIONS.ENTRY:
      return t('Entry');
    case LPR_DIRECTIONS.EXIT:
      return t('Exit');
    case LPR_DIRECTIONS.BI_DIRECTIONAL:
      return t('BI directional');
  }
};

export const getROIRegionName = (regions: string[]): string => {
  let currentLength = (regions.length ?? 0) + 1;
  let name = `Region ${currentLength}`;

  while (regions.includes(name)) {
    currentLength += 1;
    name = `Region ${currentLength}`;
  }

  return name;
};

export const sortOutGivenName = (currentName: string, nameList: string[]): string[] => {
  let nameIndex;
  const updatedNames = nameList.map((name, index) => {
    if (name === currentName) {
      nameIndex = index;
    }
    return name;
  });

  if (nameIndex !== undefined) updatedNames.splice(nameIndex, 1);

  return updatedNames;
};

export const getColorForEevaPreviewCard = (isDetected?: boolean | null) => {
  switch (isDetected) {
    case true:
      return EEVA_PREVIEW_CARD_COLORS.positive;
    case false:
      return EEVA_PREVIEW_CARD_COLORS.negative;
    default:
      return EEVA_PREVIEW_CARD_COLORS.accent;
  }
};

export const getEevaPreviewCardImageLabel = (key: string) => {
  switch (key.toLowerCase()) {
    case EEVA_ANALYSED_IMAGE_KEY.yes:
      return EEVA_ANALYSED_IMAGE_KEY_LABELS.detected;
    case EEVA_ANALYSED_IMAGE_KEY.nonBinary:
      return EEVA_ANALYSED_IMAGE_KEY_LABELS.nonBinary;
    default:
      return EEVA_ANALYSED_IMAGE_KEY_LABELS.notDetected;
  }
};

export const isAnalyticSettingsEnabled = (analyticType: string, analyticSettingsArray: UPDATED_CONFIGURATION[]) => {
  const analyticSetting = analyticSettingsArray.find((analyticSetting) => analyticSetting.id === analyticType);

  return analyticSetting?.performedOn && analyticSetting?.performedOn !== PERFORMED_ON.DISABLED;
};

interface Point {
  x: number;
  y: number;
}

interface PolygonArea {
  object: {
    points: Point[];
  };
}

/**
 * Checks if a point is inside a polygon using the ray-casting algorithm
 * @param point The point to check
 * @param polygon Array of points defining the polygon
 * @returns True if the point is inside the polygon, false otherwise
 */
export function isPointInPolygon(point: Point, polygon: Point[]): boolean {
  const { x, y } = point;
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
}

/**
 * Finds all regions that contain the given point
 * @param point The point to check
 * @param regions Array of region objects to check against
 * @returns Array of regions that contain the point
 */
export function findOverlappingRegions(point: Point, regions: PolygonArea[]): PolygonArea[] {
  return regions.filter((area) => isPointInPolygon(point, area.object.points));
}

export function calculateTextPosition(points: Point[]): Point {
  return points.reduce((closest, current) => {
    const distClosest = Math.hypot(closest.x, closest.y);
    const distCurrent = Math.hypot(current.x, current.y);
    return distCurrent < distClosest ? current : closest;
  });
}

export function truncateTextByLength(text: string, maxLength: number = 15): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength - 3) + '...';
}

export function getTextOptions(points: Point[], color: string, id: string, name: string) {
  const BACKGROUND_COLOR = '#252932';
  const OPACITY = 0.8;
  const textPoint = calculateTextPosition(points);
  const CHARACTER_PADDING = 2;
  const BASE_PADDING_Y = 15;
  const BASE_PADDING_X = 4;
  const truncatedName = truncateTextByLength(name);
  const paddedPosition = { x: textPoint.x + truncatedName.length * BASE_PADDING_X, y: textPoint.y + BASE_PADDING_Y };
  const textOptions = {
    fill: color,
    fontFamily: 'Roboto',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 20 / 14,
    charSpacing: 0,
    textAlign: 'center',
    originX: 'center',
    originY: 'center',
    selectable: false,
    objectCaching: false,
    evented: false,
  };
  const backgroundOptions = {
    rx: 4,
    ry: 4,
    fill: colorWithOpacity(BACKGROUND_COLOR, OPACITY),
    originX: 'center',
    originY: 'center',
    selectable: false,
    evented: false,
  };
  const groupOptions = {
    id: id,
    left: paddedPosition.x,
    top: paddedPosition.y,
    originX: 'center',
    originY: 'center',
    selectable: false,
    evented: false,
  };

  return { textOptions, backgroundOptions, groupOptions, truncatedName, CHARACTER_PADDING };
}

const roundCoordinates = (coordinates: number[][]): number[][] => {
  return coordinates.map((point) => point.map((value) => parseFloat(value.toFixed(4))));
};

const isConfigurationDirty = (initialConfig?: ConfigurationPatch[], updatedConfig?: ConfigurationPatch[]): boolean => {
  let isDirty = false;
  if (!initialConfig && !updatedConfig) {
    return isDirty;
  }

  let modifiedConfig = _.cloneDeep(updatedConfig) || [];
  modifiedConfig = modifiedConfig?.reduce((acc, item) => {
    const currentInitialConfig = initialConfig?.find((config) => config.creatorId === item.creatorId);
    if (currentInitialConfig) {
      if ('regions' in currentInitialConfig && currentInitialConfig.regions?.length) {
        currentInitialConfig.regions = _.sortBy(currentInitialConfig.regions, 'name');
        currentInitialConfig.regions.forEach((region) => {
          if ('polygon' in region && region.polygon?.length) region.polygon = roundCoordinates(region.polygon);
          if ('color' in region) delete (region as { color?: string }).color;
        });
      }

      if ('lines' in currentInitialConfig && currentInitialConfig.lines?.length) {
        currentInitialConfig.lines.forEach((line) => {
          if (line.coordinates.length) line.coordinates = roundCoordinates(line.coordinates);
        });
      }
      const configCopy = { ...item };

      if ('regions' in configCopy && configCopy.regions?.length) {
        configCopy.regions = _.sortBy(configCopy.regions, 'name');
        configCopy.regions.forEach((region) => {
          delete (region as { color?: string }).color;
          if ('polygon' in region && region.polygon?.length) region.polygon = roundCoordinates(region.polygon);
        });
      }
      if ('lines' in configCopy && configCopy.lines?.length) {
        configCopy.lines.forEach((line) => {
          if (line.coordinates.length) line.coordinates = roundCoordinates(line.coordinates);
        });
      }
      acc.push(configCopy);
    }
    return acc;
  }, [] as ConfigurationPatch[]);

  isDirty = !_.isEqual(initialConfig || [], modifiedConfig || []);
  return isDirty;
};

export const isSettingsDirty = (
  initialArray: UPDATED_CONFIGURATION[],
  updatedArray: UPDATED_CONFIGURATION[]
): boolean => {
  for (const setting of updatedArray) {
    const initialSetting = initialArray.find((initialSetting) => initialSetting.id === setting.id);
    if (initialSetting) {
      const isPerformedOnDirty = initialSetting.performedOn !== setting.performedOn;

      const configurationDirty =
        !isPerformedOnDirty &&
        setting.performedOn !== PERFORMED_ON.DISABLED &&
        initialSetting.performedOn !== PERFORMED_ON.DISABLED
          ? isConfigurationDirty(initialSetting?.configuration, setting?.configuration)
          : false;

      const isDirty = isPerformedOnDirty || configurationDirty;
      if (isDirty) {
        return true; // Early exit if any setting is dirty
      }
    }
  }
  return false; // No dirty settings found
};

export const getUpdatedSettingsArray = (
  initialArray: UPDATED_CONFIGURATION[],
  updatedArray: UPDATED_CONFIGURATION[]
): UPDATED_CONFIGURATION[] => {
  let settings: UPDATED_CONFIGURATION[] = [];
  updatedArray.forEach((setting) => {
    const initialSetting = initialArray.find((initialSetting) => initialSetting.id === setting.id);
    if (initialSetting) {
      const isPerformedOnDirty = initialSetting.performedOn !== setting.performedOn;
      const configurationDirty =
        !isPerformedOnDirty && setting.performedOn !== PERFORMED_ON.DISABLED
          ? isConfigurationDirty(initialSetting?.configuration, setting?.configuration)
          : false;
      const isDirty = isPerformedOnDirty || configurationDirty;
      if (isDirty) {
        settings.push(setting);
      }
    }
  });
  return settings;
};

export const getRulesMessage = (alertRulesLink: string, rulesCount: number, eventName: string) => {
  return t('This camera has {rulesLink} configured based on the {analyticsType} setup', {
    rulesLink: `<a href=${alertRulesLink}> ${tc('{rulesCount} rules', rulesCount, { rulesCount })} </a>`,
    analyticsType: eventName,
  });
};

export const getAlertRulesLink = (cameraId: string, eventType: string) =>
  `${window.location.origin}#/automations/rules/?actor=camera:${cameraId}&eventFilter.eventTypes__in=${eventType}`;

/**
 * Format given time value in the specified format in current user timezone
 * @param dateTime Date time value
 * @returns Timestamp in the format of, eg: Wed, April 2 @ 3:32:45pm
 */
export const getFormattedTimestamp = (time: number | string) => {
  const usersStore = useUsersStore();
  const format = usersStore.hour12 ? 'ddd, MMMM D @ hh:mm:ssa' : 'ddd, MMMM D @ HH:mm:ss';

  return dayjs(time).tz(usersStore.currentUserTimezone).format(format);
};
